import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app_language';
  private readonly DEFAULT_LANGUAGE = 'en';

  readonly availableLanguages: Language[] = [
    { code: 'en', name: 'languages.english', flag: 'flag:united-states' },
    { code: 'de', name: 'languages.german', flag: 'flag:germany' }
  ];

  private currentLangSubject: BehaviorSubject<string>;
  currentLang$: Observable<string>;

  // Emits when translations are loaded (and on every language change)
  private translationsReadySubject = new ReplaySubject<string>(1);
  translationsReady$ = this.translationsReadySubject.asObservable();

  constructor(private translateService: TranslateService) {
    const savedLang = this.getSavedLanguage();
    this.currentLangSubject = new BehaviorSubject<string>(savedLang);
    this.currentLang$ = this.currentLangSubject.asObservable();

    this.translateService.setDefaultLang(this.DEFAULT_LANGUAGE);

    // Subscribe to language changes and emit when ready
    this.translateService.onLangChange.subscribe((event) => {
      this.translationsReadySubject.next(event.lang);
    });

    // Load the initial language
    this.translateService.use(savedLang);
  }

  setLanguage(langCode: string): void {
    if (this.isValidLanguage(langCode)) {
      localStorage.setItem(this.STORAGE_KEY, langCode);
      this.translateService.use(langCode);
      this.currentLangSubject.next(langCode);
    }
  }

  getLanguage(): string {
    return this.currentLangSubject.value;
  }

  getCurrentLanguage(): Language | undefined {
    return this.availableLanguages.find(l => l.code === this.getLanguage());
  }

  private getSavedLanguage(): string {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved && this.isValidLanguage(saved)) {
      return saved;
    }
    return this.DEFAULT_LANGUAGE;
  }

  private isValidLanguage(langCode: string): boolean {
    return this.availableLanguages.some(l => l.code === langCode);
  }
}
